<p>
    The "sts" program is a command-line interface (CLI) that uses the Python STS library to interface with your instance.
    It enables the same functionality as the ImgSigner application. 
    You may prefer to use the CLI if you are automating actions with scripting, using a Linux workstation, or more comfortable with a text-based interface.
</p>

<div class="buffer"></div>
<hr />

<h1>Setup</h1>
<h2>Requirements</h2>
<div class="warn">
    <mat-icon class="alert-icon" svgIcon="alert-circle"></mat-icon>
    Before using sts, you must have the following software installed:
    <ul>
        <li><p><a href="https://www.python.org/downloads/">Python 3.x</a></p></li>
        <li><p><a href="https://pip.pypa.io/en/stable/installing/">pip</a></p></li>
    </ul>
</div>
<h2>Download</h2>
<div class="info">
    <p>
        To get started, download the latest version of the
        <a href="https://docs-np.sts.gus.cisco.com/main/downloads/python" target="_blank">Python STS library</a>.
    </p>
</div>
<h2>Installation</h2>
<div class="info">
    <p>
        After downloading the Python STS .whl file, install it with pip:
    </p>
    <div class="code">
        pip install ~/Downloads/ciscosts-*-py3-none-any.whl
    </div>
    <p>
        Now, invoking the "sts" command should display the following initial message:
    </p>
    <div class="code">
    <pre>
    $ sts
    [!] Configuration file "C:\Users\john\.stsconfig" does not exist
    [!] A default one has been created
    [!] Please edit it by running "sts configure"</pre>
    </div>
</div>

<h2>Configuration</h2>
<div class="info">
    <p>
        sts uses a configuration file named .stsconfig, which is located in your current user's home directory.
        When running the CLI for the first time, a default configuration file will be created if none exists.
        Before you use sts, you must replace the default values inserted here.
        You can easily do this using the sts configure command.
    </p>
    <p>
        In addition to the default profile, you may add others that can be selected with the --profile argument.
        This may be useful if you interact with multiple STS stacks, or have multiple accounts
        (e.g. separate unprivileged and administrator accounts).
    </p>
</div>

<div class="buffer"></div>
<hr />

<h1>Usage</h1>
<h2>Quick Start</h2>
<p>
    The following are some common uses of sts:
</p>
<div class="info">
    <h3>Upload a file</h3>
    <div class="code">
        <pre>$ sts upload ./test.txt
&#123;'cHash': 'awaiting verification',
'id': '5e996ed6-2884-4d76-a334-981e6f1bb81a',
'image': 'test.txt',
'oHash': '9128d61177cc551b2bc30fc0a7ec418208ddfdb8bb368e7aa10b11fa97ba21c0b48c16d65d37c3fd77b0b05a7d4b8510f7a8665ce6ca3f7075fcae9546101bda',
'phase': 'rebuild',
'scanDefinitionDate': 'null',
'scanEngine': 'null',
'sig1': 'null',
'sig1Owner': 'null',
'sig2': 'null',
'sig2Owner': 'null',
'source': 'john',
'transferring': False&#125;</pre>
    </div>
</div>
<div class="buffer"></div>
<div class="info">
    <h3>Sign a pending transfer</h3>
    <div class="code">
    <pre>$ sts transfers sign name test.txt
[>] If your private key has a passphrase, enter it. Otherwise, press &lt;Enter&gt;: 
[+] Signed transfer: 5e996ed6-2884-4d76-a334-981e6f1bb81a
    </pre>
    </div>
</div>
<div class="buffer"></div>
<div class="info">
    <h3>List pending transfers</h3>
    <div class="code">
    <pre>$ sts transfers list
[+] Found 1 transfer(s)
[&#123;'cHash': 'awaiting verification',
'id': '5e996ed6-2884-4d76-a334-981e6f1bb81a',
'image': 'test.txt',
'oHash': '9128d61177cc551b2bc30fc0a7ec418208ddfdb8bb368e7aa10b11fa97ba21c0b48c16d65d37c3fd77b0b05a7d4b8510f7a8665ce6ca3f7075fcae9546101bda',
'phase': 'rebuild',
'scanDefinitionDate': 'null',
'scanEngine': 'null',
'sig1': 'Hav9/yKKoBF2PQ4L/e3hFYS62u+g3+v7bnsTSsl/C0yCgNwG6MM143HBWv1TMJ+j6pZURXT13tHsoQgREKJ3LKfL4+0Z7yUze1ErMCtLUaw78HzvL2+4bvooOjHtU1W7Ujd
        UYuCTRdAXJV4e7jS+ZWrDY0REz5Ky/6FXU/dowQ8y6zXeDRll12WQ1akm6UlXW64r/j+3INnCw+Dkv03mrmhZrScgfMFt7TQBFuCwdeXN/QjPUUMEgDFjcjqMwIci/jkJGz
        3bZNswDIwKj8MM17QHH5g0cr0aA8bMwd4IW1iA71C7k1mdSDqA8ZB5w53oz8M6RVKeMCbhBUMJSCG/lBZjE1EeUvNHfymNFAsSaATlSiDVlk54Ksm89L22CKm2EYOzayT+I
        6g79hQSyBep/GRLktAFRUBeuf+tM6vlJpyytm+ax3yB4x+vTL8Qs30J0LshmD/R46C2lubE7fjbeYGaCIGNWkf1VfY1GjoK8eGpmzZWgjXkOaDqXKI7vQt6RIAPjf0MFVDL
        ygULFKfDBDH/GDfD6tC5d9dQP8D9Bo0RoH0QiIQvwSZSr2D3Cso/r/d3IQhdR8iSAcsT5nzHJ9ZRiD6J0AB1sOngeZRs7NNoQjGgLtc691vY1tXgW5djd+hhq2p63dnU6y6
        5RQeOFELv08TCgxQksS1uTt6hseg=',
'sig1Owner': 'john',
'sig2': 'null',
'sig2Owner': 'null',
'source': 'john',
'transferring': False&#125;]</pre>
    </div>
</div>
<div class="buffer"></div>
<div class="info">
    <h3>Reject a pending transfer</h3>
    <div class="code">
<pre>$ sts transfers reject name test.txt
[+] Rejected transfer: 5e996ed6-2884-4d76-a334-981e6f1bb81a
    </pre>
    </div>
</div>

<h2>Commands and Groups</h2>
<div class="info">
    <p>
        sts is organized in a hierarchical structure.
        It has one required positional argument, which is the top-level command or group to use.
    </p>
    <h3>Commands</h3>
    <p>
        The available top-level commands are:
    </p>
    <ul>
        <li><p>configure</p></li>
        <li><p>update</p></li>
        <li><p>upload</p></li>
    </ul>
    <h3>Groups</h3>
    <p>
        The available top-level groups are:
    </p>
    <ul>
        <li><p>alerts</p></li>
        <li><p>audit</p></li>
        <li><p>keys</p></li>
        <li><p>transfers</p></li>
    </ul>
    <h3>Nesting of commands and groups</h3>
    <p>
        Below the top-level groups are sub-commands and potentially sub-groups.
        To explore the available sub-commands and sub-groups in a group, you call the group without any additional arguments. 
    </p>
    <p>
        For example:
    </p>

    <div class="code"><pre>$ sts audit
    usage: sts audit [-h] &#123;export,list,backup&#125; ...

    positional arguments:
    &#123;export,list,backup&#125;  The command to run
        export              Export audit records
        list                List audit records
        backup              [ GROUP ] audit record backup operations

    optional arguments:
    -h, --help            show this help message and exit</pre></div>

    <p>
        As shown here, the `audit` group contains several commands as well as a sub-group related to backup operations.    
    </p>
</div>

<h2>Getting Help and Exploring the CLI</h2>
<div class="info">
    <p>
    Invoking sts with no arguments will provide you with help on how to use the CLI.
    </p>

    <div class="code"><pre>$ sts
    usage: sts [-h] [--config CONFIG] [--profile PROFILE] &#123;configure,update,upload,alerts,audit,keys,transfers&#125; ...

    positional arguments:
    &#123;configure,update,upload,alerts,audit,keys,transfers&#125;
                            The command to run
        configure           Configure the CLI
        update              Update the STS CLI
        upload              Upload a file
        alerts              [ GROUP ] alert operations
        audit               [ GROUP ] audit record operations
        keys                [ GROUP ] cryptographic and API key operations
        transfers           [ GROUP ] pending transfer operations

    optional arguments:
    -h, --help            show this help message and exit
    --config CONFIG       Path to the .stsconfig configuration file
    --profile PROFILE     Configuration profile to use</pre></div>
    <p>
        Help can also be listed with the --help argument, or -h for short.
        This help information lists required and optional arguments for any command.
    </p>
</div>